/**
 * Motion system.
 *
 * Two rules drive everything here.
 *
 * First, duration is a function of what the motion has to explain, not a single
 * global constant: a button acknowledging a press needs ~120ms, an object
 * arriving on the board needs long enough to be noticed (~260ms), and a stroke
 * that is meant to read as *someone drawing* needs to scale with its own length.
 *
 * Second, motion is optional. Everything routes through `prefersReducedMotion`,
 * and when it is on, durations collapse to zero rather than the animation being
 * skipped — so the same code path produces the final state directly.
 */

export const DURATION = {
  /** Press feedback, hover states. */
  instant: 120,
  /** Menus, panels, tooltips. */
  fast: 180,
  /** Objects entering or leaving the board. */
  normal: 260,
  /** Camera moves, which need to stay readable while they travel. */
  slow: 420,
} as const;

/** Easing curves, matched to the CSS custom properties in styles.css. */
export const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;
export const easeOutQuint = (t: number): number => 1 - (1 - t) ** 5;
export const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

/**
 * Slight overshoot, used where an object should feel like it *landed* rather
 * than slid into place. Kept gentle: on a canvas full of objects, a springy
 * overshoot on every arrival reads as noise.
 */
export function easeOutBack(t: number, overshoot = 1.24): number {
  const c = overshoot + 1;
  return 1 + c * (t - 1) ** 3 + overshoot * (t - 1) ** 2;
}

let reducedMotion = false;
let mediaQuery: MediaQueryList | null = null;

if (typeof window !== 'undefined' && window.matchMedia) {
  mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  reducedMotion = mediaQuery.matches;
  // `change` rather than the deprecated addListener; Safari has supported it
  // on MediaQueryList since 14.
  mediaQuery.addEventListener('change', (event) => {
    reducedMotion = event.matches;
  });
}

export function prefersReducedMotion(): boolean {
  return reducedMotion;
}

/** Duration to actually use, honouring the user's motion preference. */
export function duration(ms: number): number {
  return reducedMotion ? 0 : ms;
}

/** Normalised progress of an animation started at `start`. */
export function progressOf(start: number, ms: number, now: number): number {
  if (ms <= 0) return 1;
  return Math.min(1, Math.max(0, (now - start) / ms));
}

/**
 * Frame-rate independent exponential smoothing.
 *
 * `timeConstant` is the time to cover ~63% of the remaining distance. Using an
 * exponential rather than a fixed per-frame lerp means the motion looks the
 * same at 60 Hz and at 144 Hz, which matters for remote cursors: a fixed lerp
 * makes them noticeably snappier on a high-refresh display.
 */
export function smoothTowards(
  current: number,
  target: number,
  deltaMs: number,
  timeConstant = 55,
): number {
  if (reducedMotion || timeConstant <= 0) return target;
  const alpha = 1 - Math.exp(-deltaMs / timeConstant);
  const next = current + (target - current) * alpha;
  // Snap when close enough, so a cursor does not creep forever by fractions.
  return Math.abs(target - next) < 0.01 ? target : next;
}
